import '@treasury/omega/components/omega-button.js';
import '@treasury/omega/components/omega-drag-and-drop.js';
import '@treasury/omega/components/omega-radio-group.js';
import '@treasury/omega/components/omega-select.js';
import '@treasury/omega/components/omega-tabs.js';
import '@treasury/omega/components/omega-tooltip.js';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TmBaseComponent } from '../tm-base.component';
import { fontAwesome } from './font-awesome';

const glossary = [
    {
        title: 'Payment Information',
        label: 'Payment Info',
        id: 'payInfo',
        terms: [
            { name: 'Wire Company Name', text: 'Originators information' },
            { name: 'Debit Account', text: 'Account originating the wire' },
            { name: 'Beneficiary', text: 'Recipient of funds' },
            { name: 'Frequency', text: 'One-time or recurring' },
            { name: 'Additional Information', text: 'Optional note' },
            { name: 'Effective Date', text: 'Date of payment' },
            { name: 'Wire Amount', text: 'Amount' },
            { name: 'Reference Beneficiary', text: 'Additional information on the beneficiary' },
            { name: 'Purpose', text: 'Reason for payment' },
        ],
    },
    {
        title: 'Beneficiary Information',
        label: 'Beneficiary Info',
        id: 'beneficiaryInfo',
        terms: [
            { name: 'Bank ID', text: 'Beneficiary bank routing number' },
            { name: 'Bank Name', text: 'Beneficiary bank name' },
            { name: 'Bank City', text: 'Beneficiary bank city' },
            { name: 'Bank State', text: 'Beneficiary bank state' },
            { name: 'Account Number', text: 'Account number of the recipient' },
            { name: 'Notes', text: 'Optional information' },
            { name: 'Name', text: 'Recipient name' },
            { name: 'Street Address', text: 'Recipient address' },
            { name: 'City', text: 'Recipient city' },
            { name: 'State', text: 'Recipient state' },
            { name: 'Zip Code', text: 'Recipient zip code' },
        ],
    },
    {
        title: 'Domestic Intermediary',
        label: 'Domestic Intermediary Info',
        id: 'domesticIntermediaryInfo',
        terms: [
            { name: 'Bank ID', text: 'Intermediary bank routing number' },
            { name: 'Bank Name', text: 'Intermediary bank name' },
            { name: 'Bank City', text: 'Intermediary bank city' },
            { name: 'Bank State', text: 'Intermediary bank state' },
        ],
    },
];

const tagName = 'tm-file-format-designer';

@customElement(tagName)
export class TmFileFormatDesigner extends TmBaseComponent {
    @property()
    public format = {
        title: 'New File Format',
        name: '',
        type: 'Delimited',
        excludeHeaderRows: 0,
        excludeFooterRows: 0,
        itemAmountFormat: 'DecimalIncluded',
        effectiveDateFormat: 'mm/dd/yyyy',
        containers: [],
        tiles: [],
    };

    @property({ type: Array })
    public glossary = glossary;

    @property({ type: Boolean })
    public entitlement = true;

    @property({ type: Boolean })
    public loading = false;

    @property()
    public itemAmountFormats = [
        { label: 'Decimal Included', value: 'DecimalIncluded' },
        { label: 'Whole Dollar (798 = 798.00)', value: 'WholeDollar' },
        { label: 'Implied Decimal (798 = 7.98)', value: 'ImpliedDecimal' },
    ];

    @property({ type: Number })
    public activeGlossaryTab = 0;

    @property()
    public validators = {
        name: true,
        excludeHeaderRows: true,
        excludeFooterRows: true,
    };

    @property({ type: Boolean })
    public hasKeyed = false;

    public firstUpdated() {
        this.validators = { ...this.validators, name: this.format.name?.length > 0 };
    }

    public updated() {
        this.validateFormat();
    }

    public setActiveTab(e: CustomEvent<{ activeTab: string }>) {
        this.format.type = e.detail.activeTab;
        this.format = { ...this.format };
    }

    public validateInput(
        input: HTMLInputElement,
        property: keyof typeof this.validators,
        validator: (value: string) => boolean
    ) {
        this.hasKeyed = true;
        this.format[property] = input.value as never;
        if (validator(input.value)) {
            this.validators = { ...this.validators, [property]: true };
            input.classList.remove('file-format-input-invalid');
        } else {
            this.validators = { ...this.validators, [property]: false };
            input.classList.add('file-format-input-invalid');
        }
    }

    public validateFormat() {
        if (!this.format.tiles) {
            return false;
        }
        return (
            !this.format.tiles.some((tile: { active: string }) => tile.active === 'true') ||
            Object.values(this.validators).some(value => !value)
        );
    }

    public toggleGlossary() {
        this.shadowRoot?.querySelector('.modal-background')?.classList.toggle('show');
        this.shadowRoot?.querySelector('#file-format-glossary')?.classList.toggle('show');
    }

    public switchGlossaryTab(event: CustomEvent<{ activeTab: string }>) {
        this.activeGlossaryTab = event.detail.activeTab as unknown as number;
        this.shadowRoot
            ?.querySelectorAll('.tab-content')
            ?.forEach(content => content.classList.remove('show-tab-content'));

        this.shadowRoot
            ?.querySelector(`#${this.activeGlossaryTab}-content`)
            ?.classList.add('show-tab-content');
    }

    public toggleLegend() {
        this.shadowRoot?.querySelector('.file-format-legend-detail')?.classList.toggle('show');
    }

    public render() {
        return html` <div class="file-format-container">
                <section class="file-format-title-section">
                    <header>
                        <h2 class="file-format-title">${this.format.title}</h2>
                    </header>
                    <nav class="file-format-navigation" role="navigation">
                        <label for="tabs">Upload Format</label>
                        <omega-tabs
                            .tabs=${[
                                { label: 'Delimited', id: 'Delimited' },
                                { label: 'Fixed Position', id: 'FixedPosition' },
                            ]}
                            label="Upload Format"
                            activeTab=${this.format.type}
                            @switchTab=${this.setActiveTab}
                        ></omega-tabs>
                    </nav>
                </section>
                <div role="tabpanel">
                    <section class="file-format-name-section">
                        <div class="file-format-input-wrapper form-field">
                            <label for="file-format-name">Format Name &#42;</label>
                            <input
                                type="text"
                                name="file-format-name"
                                id="file-format-name"
                                class="file-format-input"
                                .value="${this.format.name}"
                                @keyup=${(e: KeyboardEvent) =>
                                    this.validateInput(
                                        e.target as HTMLInputElement,
                                        'name',
                                        value => /^[\s\w-]+$/.test(value) && value.length <= 100
                                    )}
                            />
                            ${!this.validators.name && this.hasKeyed
                                ? html`<small class="input-invalid-text"
                                      ><i class="fa fa-exclamation-circle"></i>&nbsp;${this.format
                                          .name
                                          ? html`Format Name must be less than 100 alphanumeric
                                            characters.`
                                          : html`Format name is required.`}</small
                                  >`
                                : nothing}
                        </div>
                        <span
                            >Text qualifier is identified by a single quote (') and a field
                            delimiter is identified by a comma (,)<br />If this format will be used
                            for files sent via FTP, the name should not contain any spaces or
                            underscores.</span
                        >
                    </section>
                    <section class="file-format-options-section">
                        <header class="file-format-section-header">
                            <p>
                                ${this.format.type === 'Delimited'
                                    ? this.format.type
                                    : 'Fixed Position'}
                            </p>
                            <omega-tooltip
                                icon="info-circle"
                                message="${this.format.type === 'Delimited'
                                    ? 'To add or edit a Delimited file upload format, select the order of the columns in your file.'
                                    : 'To add or edit a Fixed Position file upload format, select the position of the columns in your file.'}"
                            ></omega-tooltip>
                        </header>
                        <div class="file-format-options-row">
                            <div class="exclude-inputs">
                                <div class="file-format-input-wrapper form-field">
                                    <label for="file-format-exclude-headers"
                                        >Exclude Header Rows</label
                                    >
                                    <div class="input-container">
                                        <input
                                            type="number"
                                            min="0"
                                            max="99"
                                            name="file-format-exclude-headers"
                                            id="file-format-exclude-headers"
                                            class="file-format-input"
                                            .value=${this.format.excludeHeaderRows}
                                            @change=${(e: Event) =>
                                                this.validateInput(
                                                    e.target as HTMLInputElement,
                                                    'excludeHeaderRows',
                                                    value => Number(value) < 100
                                                )}
                                        />

                                        <omega-tooltip
                                            icon="info-circle"
                                            message="If your wire file contains a header record or records, please enter the number of rows to be excluded"
                                        ></omega-tooltip>
                                        ${!this.validators.excludeHeaderRows
                                            ? html`<small class="input-invalid-text"
                                                  ><i class="fa fa-exclamation-circle"></i
                                                  >&nbsp;Header Rows must be less than 100.</small
                                              >`
                                            : nothing}
                                    </div>
                                </div>
                                <div class="file-format-input-wrapper form-field">
                                    <label for="file-format-exclude-footers"
                                        >Exclude Footer Rows</label
                                    >
                                    <div class="input-container">
                                        <input
                                            type="number"
                                            min="0"
                                            max="99"
                                            name="file-format-exclude-footers"
                                            id="file-format-exclude-footers"
                                            class="file-format-input"
                                            .value=${this.format.excludeFooterRows}
                                            @change=${(e: Event) =>
                                                this.validateInput(
                                                    e.target as HTMLInputElement,
                                                    'excludeFooterRows',
                                                    value => Number(value) < 100
                                                )}
                                        />
                                        <omega-tooltip
                                            icon="info-circle"
                                            message="If your wire file contains a footer record or records, please enter the number of rows to be excluded"
                                        ></omega-tooltip>
                                        ${!this.validators.excludeFooterRows
                                            ? html`<small class="input-invalid-text"
                                                  ><i class="fa fa-exclamation-circle"></i
                                                  >&nbsp;Footer Rows must be less than 100.</small
                                              >`
                                            : nothing}
                                    </div>
                                </div>
                            </div>
                            <omega-radio-group
                                id="item-amount-selection"
                                label="Item Amount"
                                orientation="column"
                                value=${this.format.itemAmountFormat}
                                name="item amount"
                                .radios=${this.itemAmountFormats}
                                @change=${this._updateItemAmountFormat}
                            ></omega-radio-group>
                            <div class="file-format-input-wrapper date-wrapper form-field">
                                <label for="file-format-effective-date-format"
                                    >Effective Date Format</label
                                >
                                <select
                                    aria-label="Effective Date Format"
                                    name="file-format-effective-date-format"
                                    class="file-format-select file-format-input"
                                    @change=${(e: Event) => {
                                        this.format.effectiveDateFormat = (
                                            e.target as HTMLSelectElement
                                        ).value;
                                    }}
                                >
                                    <option ?selected=${this._isSelectedDateFormat('mm/dd/yyyy')}>
                                        mm/dd/yyyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('mm/dd/yy')}>
                                        mm/dd/yy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('mm-dd-yyyy')}>
                                        mm-dd-yyyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('mm-dd-yy')}>
                                        mm-dd-yy
                                    </option>

                                    <option ?selected=${this._isSelectedDateFormat('dddyy')}>
                                        dddyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('dddyyyy')}>
                                        dddyyyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('ddmmyy')}>
                                        ddmmyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('ddyymm')}>
                                        ddyymm
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('ddmmyyyy')}>
                                        ddmmyyyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('mmddyy')}>
                                        mmddyy
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('mmyydd')}>
                                        mmyydd
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yyddd')}>
                                        yyddd
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yyddmm')}>
                                        yyddmm
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yymmdd')}>
                                        yymmdd
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yyyyddd')}>
                                        yyyyddd
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yyyyddmm')}>
                                        yyyyddmm
                                    </option>
                                    <option ?selected=${this._isSelectedDateFormat('yyyymmdd')}>
                                        yyyymmdd
                                    </option>
                                </select>
                            </div>
                        </div>
                    </section>
                    <section class="file-format-column-order-section">
                        <div class="file-format-column-order-section-header">
                            <span class="file-format-column-order-section-title"
                                >${this.format.type === `Delimited`
                                    ? `Column Order`
                                    : `Fixed
                                      Positioning`}</span
                            >
                            <div class="file-format-column-glossary">
                                <button
                                    id="file-format-designer-glossary-button"
                                    @click=${this.toggleGlossary}
                                >
                                    Glossary
                                </button>
                                <span id="glossary-divider"></span>
                                <div class="file-format-legend-detail">
                                    <div class="file-format-legend-detail-header">
                                        <i
                                            class="fa fa-times"
                                            role="button"
                                            aria-label="Close"
                                            @click=${this.toggleLegend}
                                        ></i>
                                    </div>
                                    ${this.glossary.map(function (glossaryItem) {
                                        const swatchClasses: Record<string, string> = {
                                            payInfo: 'payment-information',
                                            beneficiaryInfo: 'beneficiary-information',
                                            domesticIntermediaryInfo: 'domestic-intermediary',
                                        };

                                        return html`<span
                                                class="${swatchClasses[
                                                    glossaryItem.id as keyof typeof swatchClasses
                                                ]}-swatch swatch"
                                            ></span
                                            ><span> ${glossaryItem.title}</span><br />`;
                                    })}
                                </div>
                                <div
                                    class="file-format-legend"
                                    role="button"
                                    aria-label="Toggle Legend"
                                    @click=${this.toggleLegend}
                                >
                                    <div class="payment-information-swatch swatch"></div>
                                    <div class="beneficiary-information-swatch swatch"></div>
                                    <div class="domestic-intermediary-swatch swatch"></div>
                                </div>
                            </div>
                        </div>
                        <omega-drag-and-drop
                            .containers=${this.format.containers}
                            .tiles=${this.format.tiles}
                            .type=${this.format.type}
                            @tilesUpdated=${this._updateFormat}
                        ></omega-drag-and-drop>
                    </section>
                </div>
                <footer class="file-format-action-bar action-bar">
                    <omega-button
                        id="file-format-save-button"
                        type="primary"
                        ?disabled="${this.validateFormat() || !this.entitlement}"
                        ?loading=${this.loading}
                        @click=${this._onSave}
                    >
                        Save
                    </omega-button>
                    <omega-button id="file-format-cancel-button" @click=${this._onCancel}
                        >Cancel</omega-button
                    >
                </footer>
            </div>
            <div class="modal-background"></div>
            <div id="file-format-glossary" class="modal" tabindex="0">
                <div class="modal-header">
                    <span class="modal-header-title">Wire Template Glossary of Terms</span>
                    <omega-button
                        type="icon"
                        icon="times"
                        hideLabel="always"
                        @click=${this.toggleGlossary}
                    >
                        Close
                    </omega-button>
                </div>
                <omega-tabs
                    .tabs=${this.glossary}
                    label="Glossary Term Groups"
                    @switchTab=${this.switchGlossaryTab}
                ></omega-tabs>
                ${this.glossary.map(
                    section => html`
                        <div id=${`${section.id}-content`} class="tab-content">
                            <ul class="file-format-glossary-list">
                                ${section.terms.map(
                                    term => html`
                                        <li>
                                            <span class="glossary-term-name">${term.name}</span> -
                                            ${term.text}
                                        </li>
                                    `
                                )}
                            </ul>
                        </div>
                    `
                )}
            </div>`;
    }

    private _updateFormat(e: CustomEvent<{ tiles: never[] }>) {
        this.format = { ...this.format, tiles: e.detail.tiles };
    }

    private _updateItemAmountFormat(e: CustomEvent) {
        const target = e.target as HTMLInputElement | null;
        if (target) {
            this.format.itemAmountFormat = target.value;
        }
    }

    private _onSave() {
        const saveEvent = new CustomEvent('save', {
            bubbles: true,
            composed: true,
            detail: { format: this.format },
        });
        this.dispatchEvent(saveEvent);
    }

    private _onCancel() {
        const cancelEvent = new CustomEvent('cancel', { bubbles: true, composed: true });
        this.dispatchEvent(cancelEvent);
    }

    private _isSelectedDateFormat(value: string) {
        return this.format.effectiveDateFormat === value;
    }

    public static get styles() {
        return [
            fontAwesome,
            css`
                :host {
                    display: block;
                    padding: 16px;
                }
                label {
                    font-weight: 500;
                    font-size: 14px;
                    color: #545454;
                }

                .file-format-container {
                    background: #e6e6e6;
                    padding: 1px 0 1px 0;
                    border-radius: 4px;
                    margin-bottom: 50px;
                }

                .file-format-input-row {
                    margin: 15px 5px;
                    flex-grow: 1;
                }

                .file-format-input {
                    width: 80% !important;
                    height: 32px !important;
                    padding-left: 10px;
                    font-size: 12px;
                    margin-bottom: 3px;
                    border: 1px solid rgb(199, 199, 199);
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    -ms-appearance: none;
                    -o-appearance: none;
                    appearance: none;
                    border-radius: 3px;
                    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
                    transition:
                        border-color ease-in-out 0.15s,
                        box-shadow ease-in-out 0.15s;
                }

                .file-format-input:focus {
                    outline: none;
                    border: 1px solid var(--primary-lighten-100);
                    box-shadow: 0 2px 4px 0 rgba(102, 175, 233, 0.6);
                }

                .file-format-input-invalid {
                    border: 1px solid var(--error) !important;
                    background-color: rgba(204, 16, 16, 0.03) !important;
                    box-shadow: 0 0 0 1px rgb(193, 15, 0, 0.2) !important;
                    transition: all 200ms cubic-bezier(0, 0, 0.29, 0.75) !important;
                }

                .input-invalid-text {
                    color: var(--error);
                    font-size: 11px;
                }

                .file-format-input-wrapper {
                    position: relative;
                    display: flex;
                    flex-grow: 1;
                    max-width: 435px;
                    flex-flow: row wrap;
                    height: 60px;
                    margin: 15px 5px;
                    align-items: center;
                }

                .file-format-name-section .file-format-input-wrapper {
                    margin-top: 0;
                }

                .date-wrapper {
                    max-width: 180px;
                }

                .file-format-options-section .file-format-input-wrapper {
                    display: block;
                    flex-grow: 0;
                    flex-shrink: 1;
                }

                .file-format-input-wrapper .input-container {
                    display: flex;
                    align-items: center;
                    width: 170px;
                }

                .file-format-container section {
                    background: white;
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;
                    margin: 4px 10px;
                    padding: 10px;
                }

                section.file-format-title-section {
                    min-height: 83px;
                    margin: 0;
                    padding: 10px 24px 6px;
                    border-radius: 0;
                    border: none;
                }

                .file-format-title-section h2 {
                    font-size: 16px;
                    margin: 0;
                    font-weight: 500;
                }

                .file-format-section-header p {
                    display: inline-block;
                    font-size: 14px;
                    text-transform: capitalize;
                    font-weight: 500;
                    margin: 8px 5px;
                }

                .file-format-navigation {
                    position: relative;
                    bottom: -18px;
                    display: flex;
                    justify-items: flex-end;
                }

                .file-format-navigation label {
                    line-height: 30px;
                    padding-right: 5px;
                    font-weight: 500;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 125px;
                    font-size: 13px;
                    color: #545454;
                }

                .tab-content {
                    display: none;
                }

                .tab-content.show-tab-content {
                    display: block;
                    border-top: 1px solid #d4d2d2;
                }

                .file-format-name-section {
                    display: flex;
                    flex-wrap: wrap;
                    min-height: 100px;
                    align-items: center;
                }

                .file-format-name-section span {
                    font-size: 13px;
                }

                #file-format-name {
                    min-width: 300px;
                }

                .file-format-options-row {
                    display: flex;
                    justify-content: space-between;
                }

                .file-format-options-row label {
                    font-weight: 400;
                    color: #444444;
                }

                .file-format-select {
                    padding: 6px 10px;
                    margin-bottom: 3px;
                    border: 1px solid rgb(199, 199, 199);
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    -ms-appearance: none;
                    -o-appearance: none;
                    appearance: none;
                    border-radius: 3px;
                    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
                    transition:
                        border-color ease-in-out 0.15s,
                        box-shadow ease-in-out 0.15s;
                    background: transparent;
                    background-image: url("data:image/svg+xml;utf8,<svg fill='black' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
                    background-repeat: no-repeat;
                    background-position-x: 100%;
                    background-position-y: 4px;
                }

                .file-format-column-order-section-header {
                    display: flex;
                    justify-content: space-between;
                }
                .file-format-column-order-section-title {
                    font-weight: 500;
                    font-size: 14px;
                    color: #545454;
                }

                .file-format-column-glossary {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .file-format-column-glossary button {
                    cursor: pointer;
                    font-family: inherit;
                    font-size: 13px;
                    color: var(--primary);
                    border: none;
                    background: none;
                    padding: 0;
                }

                #glossary-divider {
                    border-right: 1px solid #e0e0e0;
                    margin: 0 8px;
                    height: 20px;
                }

                /* LEGEND */
                .file-format-legend {
                    position: relative;
                    display: inline-block;
                    cursor: pointer;
                    height: 25px;
                }

                .file-format-legend:focus {
                    outline: none;
                }

                .file-format-legend-detail {
                    position: absolute;
                    bottom: -15px;
                    right: -115px;
                    width: 200px;
                    background: #fff;
                    border: 1px solid rgba(0, 101, 207, 0.5);
                    box-shadow: 0 0 10px 0 rgba(57, 57, 57, 0.5);
                    border-radius: 4px;
                    padding: 5px 11px 15px 11px;
                    opacity: 0;
                    z-index: -10;
                    transform: translate(-50%, -50%) rotateX(-180deg);
                    transition: all 0.25s cubic-bezier(0.6, -0.28, 0.735, 0.045);
                }

                .file-format-legend-detail.show {
                    opacity: 1;
                    z-index: 100;
                    transform: translate(-50%, -50%) rotateX(0deg);
                    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                .file-format-legend-detail-header {
                    display: flex;
                    justify-content: flex-end;
                    color: rgb(168, 157, 157);
                    font-size: 14px;
                    margin-bottom: 3px;
                }

                .swatch {
                    position: relative;
                    display: inline-block;
                    height: 7px;
                    width: 7px;
                    border-radius: 50%;
                }

                .payment-information-swatch {
                    background-color: #19c466;
                }

                .beneficiary-information-swatch {
                    background-color: #00bde9;
                }

                .domestic-intermediary-swatch {
                    background-color: #a5a5a5;
                }

                .file-format-legend .domestic-intermediary-swatch {
                    top: 9px;
                    right: 15.5px;
                }

                .action-bar {
                    position: fixed;
                    bottom: 0;
                    width: calc(100% - 32px);
                    height: 50px;
                    background: white;
                    border-top: 1px solid #e0e0e0;
                }

                .action-bar::after {
                    /* dirty dirty hacks */
                    content: '';
                    position: absolute;
                    left: 0;
                    right: 0;
                    top: 50px;
                    height: 30px;
                    background: #eeeeee;
                }

                /* GLOSSARY (MODAL) */
                .modal {
                    position: fixed;
                    box-shadow: 0px 0px 8px 0px #333;
                    text-align: left;
                    background: #fff;
                    padding: 5px 20px;
                }

                .modal-background {
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    background: rgba(9, 17, 9, 0.75);
                    opacity: 0;
                    z-index: -1;
                    height: 100%;
                    width: 100%;
                    transition: all 0.5s cubic-bezier(0.6, -0.28, 0.735, 0.045);
                }

                .modal-background.show {
                    position: fixed;
                    display: block;
                    opacity: 1;
                    z-index: 10;
                    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header-title {
                    width: 100%;
                    text-align: center;
                    font-size: 16px;
                    color: #5a5b5a;
                    font-weight: 600;
                }

                .modal-header button {
                    flex-shrink: 2;
                }

                #file-format-glossary {
                    z-index: -1;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) scale(0);
                    transition: all 0.5s cubic-bezier(0.6, -0.28, 0.735, 0.045);
                    min-height: 400px;
                    width: 50%;
                    min-width: 525px;
                    max-width: 615px;
                    border-radius: 4px;
                }

                #file-format-glossary.show {
                    z-index: 11;
                    transform: translate(-50%, -50%) scale(1);
                    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }

                #file-format-glossary ul {
                    list-style: none;
                    padding: 0 10px;
                }

                #file-format-glossary li {
                    margin-top: 5px;
                }

                #file-format-glossary .glossary-term-name {
                    font-weight: bold;
                }
                .exclude-inputs {
                    display: flex;
                }
                radio-group {
                    margin: 15px 5px;
                    --radio-group-flex-direction: row;
                    flex-shrink: 0;
                    --text-secondary: #444444;
                }
                @media (max-width: 1200px) {
                    radio-group {
                        --radio-group-flex-direction: column;
                        margin-left: 24px;
                    }
                    .file-format-options-row {
                        justify-content: flex-start;
                    }
                }
                @media (max-width: 900px) {
                    .exclude-inputs {
                        display: block;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tm-file-format-designer': TmFileFormatDesigner;
    }
}
